import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AuthService } from '../../../../core/services/auth.service';
import { Subject, Subscription } from 'rxjs';
import { UserDAO } from '../../../../core/services/dao/userDAO';
import { ContainsSubscriptions } from '../../../../core/services/interfaces/contains-subscriptions';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { unsubscribeFromAll } from '../../../../core/pure-functions/rx-helper';
import { ConstantMan } from '../../../../core/services/constantMan';
import * as moment from 'moment';
import { CharterDAO } from '../../../../core/services/dao/charterDAO';
import * as intlTelInput from 'intl-tel-input';

@Component({
  selector: 'app-customer-account-dialog',
  templateUrl: './customer-account-dialog.component.html',
  styleUrls: ['./customer-account-dialog.component.scss'],
})
export class CustomerAccountDialogComponent
  implements OnInit, OnDestroy, ContainsSubscriptions, AfterViewInit
{
  @ViewChild('intlTelInput') private _inputElement!: ElementRef;
  private _intlTelInput: any;
  public E164PhoneNumber!: string | null;
  signUpForm: FormGroup;
  model: ISignUpFormModel;
  confirmSocialModel: IConfirmSocialInfoModel = {
    phoneNumber: '',
    firstName: '',
    lastName: '',
    email: '',
    dobYear: null,
    dobMonth: null,
    dobDay: null,
  };
  phoneVerifyModel: PhoneVerifyModel = {
    code: null,
  };
  passwordsMustMatch = false;
  changeNumberRequest: ChangeNumberRequest = {
    phoneNumber: null,
  };
  createYourAccountLoading: boolean;
  subs: Subscription[] = [];
  toVerifyEmail: Subject<string> = new Subject<string>();

  loginButtonLoading = false;
  customerId: string;
  showLoginError = false;
  loginForm: FormGroup;
  submitted = false;
  googleUrl: string;

  accountView = CustomerAccountDialogView.SIGNUP;
  phoneMask = ConstantMan.PATTERNS.PHONE;
  days: number[] = [];
  months: string[] = [];
  years: number[] = [];
  userIdAfterAuthEvent: number = null;
  showNumberChangeField = false;
  vesselId: number;
  authType = 'Email';
  isMobile: boolean;
  useLoginInstead: string;
  isErrorVisible: boolean;
  titleText: string;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<CustomerAccountDialogComponent>,
    private authService: AuthService,
    private userDAO: UserDAO,
    private charterDAO: CharterDAO,
    private route: ActivatedRoute,
    private router: Router,
    private ngZone: NgZone,
  ) {
    this.constructDobSelections();
    this.isMobile = window && window.screen && window.screen.width < 960;
  }

  public ngAfterViewInit(): void {
    const intlTelInputInstance = intlTelInput;
    this._intlTelInput = intlTelInputInstance(
      this._inputElement?.nativeElement,
      { preferredCountries: ['us', 'mx'] },
    );
  }

  get intlTelInput(): any {
    return this._intlTelInput;
  }

  get internationalPhoneNumber(): string {
    return this.model.phoneNumber;
  }

  set internationalPhoneNumber(value: string) {
    if (!!value) {
      this._intlTelInput.setNumber(value);
    }
    this.model.phoneNumber = value;
    this.i18nizePhoneNumber();
  }

  public i18nizePhoneNumber(): void {
    this.E164PhoneNumber = null;
    if (this._intlTelInput.isValidNumber()) {
      this.E164PhoneNumber = this._intlTelInput.getNumber();
    }
  }

  hasInvalidPhoneNumber(): boolean {
    if (this._intlTelInput && this._intlTelInput.isValidNumber()) {
      return false;
    }
    return true;
  }

  ngOnInit() {
    this.setTitleText();
    this.initForm();
    this.setComponentData();
    this.getGoogleAuthUrl();
    const user = this.userDAO.getCurrentUser();
    if (user && !user.phoneConfirmed && user.phoneNumber) {
      this.accountView = CustomerAccountDialogView.CONFIRM_PHONE_NUMBER;
      this.userIdAfterAuthEvent = user.id;
      this.model.phoneNumber = user.phoneNumber;
    } else if (user && !user.phoneNumber) {
      this.accountView = CustomerAccountDialogView.CONFIRM_SOCIAL_INFO;
      this.userIdAfterAuthEvent = user.id;
    }
  }

  createAccountClick() {
    if (!this.submitted) {
      // For some reason keydown.enter triggers this method twice and throws a 409 conflict,
      // so this is a quick fix for now
      this.submitted = true;
      if (this.model.password === this.model.confirmPassword) {
        const dob = new Date(
          this.model.dobYear,
          months.indexOf(this.model.dobMonth),
          this.model.dobDay,
        );
        const emailUser = {
          birthday: dob.toISOString(),
          firstName: this.model.firstName,
          lastName: this.model.lastName,
          password: this.model.password,
          email: this.model.email,
          username: this.model.email,
          phoneNumber: this.E164PhoneNumber,
        };

        const createSub = this.userDAO
          .createEmailAccountForNewCustomer(emailUser)
          .subscribe(
            (createUserRes) => {
              const authSub = this.authService
                .login(this.model.email, this.model.password)
                .subscribe(
                  (user: any) => {
                    if (user) {
                      const charterUpdateSub = this.charterDAO
                        .updateExistingChartersForNewUser(createUserRes.id)
                        .subscribe((res) => {});
                      this.subs.push(charterUpdateSub);
                      this.userIdAfterAuthEvent = createUserRes.id;
                      const phoneSub = this.userDAO
                        .sendSmsForPhoneVerification(this.userIdAfterAuthEvent)
                        .subscribe((phoneVerifySent) => {
                          this.accountView =
                            CustomerAccountDialogView.CONFIRM_PHONE_NUMBER;
                        });
                      this.subs.push(phoneSub);
                    }
                  },
                  (error1) => {
                    console.error(error1);
                  },
                );
              this.subs.push(authSub);
            },
            (error2) => {
              console.error(error2);
              this.submitted = false;
              this.createYourAccountLoading = false;
              // todo - update ui with error state
              this.useLoginInstead = error2.error.message;
            },
          );
        this.subs.push(createSub);
      } else {
        console.log('passwords must match');
        this.passwordsMustMatch = true;
      }
    }
  }

  signupFormGenericChangeEvent(): void {
    this.submitted = false;
  }

  ngOnDestroy() {
    unsubscribeFromAll(this.subs);
  }

  closeClick(): void {
    this.dialogRef.close(false);
  }

  modalWidth(): string {
    if (this.isMobile) {
      return '100%';
    }
    if (this.accountView === CustomerAccountDialogView.LOGIN) {
      return '400px';
    } else if (this.accountView === CustomerAccountDialogView.SIGNUP) {
      return '700px';
    } else if (
      this.accountView === CustomerAccountDialogView.CONFIRM_PHONE_NUMBER
    ) {
      return '400px';
    } else {
      return '700px';
    }
  }

  confirmCode(): void {
    console.log(this.phoneVerifyModel.code);
    this.userDAO
      .smsConfirmPhone(this.phoneVerifyModel.code, this.userIdAfterAuthEvent)
      .subscribe((phoneConfirmed) => {
        console.log(phoneConfirmed);
        if (phoneConfirmed.success && this.userIdAfterAuthEvent) {
          this.dialogRef.close(true);
        } else {
          this.isErrorVisible = true;
          console.log('phone code confirmation failed');
        }
      });
  }

  resendCode(): void {
    let phoneNumber;
    if (this.changeNumberRequest && this.changeNumberRequest.phoneNumber) {
      this.model.phoneNumber = this.changeNumberRequest.phoneNumber;
    }
    phoneNumber = this.model.phoneNumber;

    this.userDAO
      .updateFromPartial(this.userIdAfterAuthEvent, {
        phoneNumber: phoneNumber,
      })
      .subscribe((updatedNumber) => {
        const phoneSub = this.userDAO
          .sendSmsForPhoneVerification(this.userIdAfterAuthEvent)
          .subscribe((phoneVerifySent) => {
            this.showNumberChangeField = false;
          });
        this.subs.push(phoneSub);
      });
  }

  changeNumber(): void {
    this.showNumberChangeField = !this.showNumberChangeField;
  }

  confirmSocialInfoClick(): void {
    console.log('confirm social click');
    if (this.userIdAfterAuthEvent) {
      const dob = new Date(
        this.confirmSocialModel.dobYear,
        months.indexOf(this.confirmSocialModel.dobMonth),
        this.confirmSocialModel.dobDay,
      );
      const sub = this.userDAO
        .updateFromPartial(this.userIdAfterAuthEvent, {
          phoneNumber: this.E164PhoneNumber,
          firstName: this.confirmSocialModel.firstName,
          lastName: this.confirmSocialModel.lastName,
          email: this.confirmSocialModel.email,
          birthday: dob.toISOString(),
        })
        .subscribe((updatedUserFromPartial) => {
          const phoneSub = this.userDAO
            .sendSmsForPhoneVerification(this.userIdAfterAuthEvent)
            .subscribe((phoneVerifySent) => {
              this.accountView = CustomerAccountDialogView.CONFIRM_PHONE_NUMBER;
            });
          this.subs.push(phoneSub);
        });
      this.subs.push(sub);
    }
  }

  login() {
    this.loginButtonLoading = true;
    this.showLoginError = false;
    const authSub = this.authService
      .login(this.model.email, this.model.password)
      .subscribe(
        (user: any) => {
          this.dialogRef.close(true);
        },
        (error1) => {
          console.error(error1);
          this.showLoginError = true;
          this.loginButtonLoading = false;
        },
      );
    this.subs.push(authSub);
  }

  public emailBlurred(emailString: string) {
    this.toVerifyEmail.next(emailString); // add to subject string, verification handled through [enrollEmailDuplicate] directive
  }

  public emailKeyupHandler(emailString: string) {
    this.toVerifyEmail.next(emailString); // add to subject string, verification handled through [enrollEmailDuplicate] directive
  }

  lessThan18(): boolean {
    const bday = moment(
      this.model.dobYear + '-' + this.model.dobMonth + '-' + this.model.dobDay,
      'YYYY-MMMM-DD',
    );
    const eighteenAgo = moment().subtract(18, 'years');
    return bday.isAfter(eighteenAgo);
  }

  confirmSocialLessThan18(): boolean {
    if (
      !this.confirmSocialModel.dobYear ||
      !this.confirmSocialModel.dobMonth ||
      !this.confirmSocialModel.dobDay
    ) {
      return true;
    }
    return moment(
      this.confirmSocialModel.dobYear +
        '-' +
        this.confirmSocialModel.dobMonth +
        '-' +
        this.confirmSocialModel.dobDay,
      '',
    ).isAfter(moment().subtract(18, 'years'));
  }

  confirmSocialModelInvalid(): boolean {
    return (
      this.confirmSocialLessThan18() ||
      !validateEmail(this.confirmSocialModel.email)
    );
  }

  toggleSignupLoginView(): void {
    if (this.accountView === CustomerAccountDialogView.LOGIN) {
      this.accountView = CustomerAccountDialogView.SIGNUP;
    } else {
      this.accountView = CustomerAccountDialogView.LOGIN;
    }
  }

  private constructDobSelections(): void {
    for (let i = 1; i < 32; i++) {
      this.days.push(i);
    }
    for (let i = 2020; i > 1900; i--) {
      this.years.push(i);
    }
    this.months = months;
  }

  private setAfterOauthState(userId: number): void {
    this.accountView = CustomerAccountDialogView.CONFIRM_SOCIAL_INFO;
    this.userIdAfterAuthEvent = userId;
    console.log(this.userIdAfterAuthEvent);
    this.setConfirmSocialModel();
  }

  private setConfirmSocialModel(): void {
    if (this.userIdAfterAuthEvent) {
      this.userDAO.loadById(this.userIdAfterAuthEvent).subscribe((userData) => {
        const user = userData;
        console.log(user);
        this.confirmSocialModel.dobYear = parseInt(
          moment(user.birthday).format('YYYY'),
        );
        this.confirmSocialModel.dobMonth = getFullMonthName(user.birthday);
        this.confirmSocialModel.dobDay = parseInt(
          moment(user.birthday).format('D'),
        );
        this.confirmSocialModel.firstName = user.firstName;
        this.confirmSocialModel.lastName = user.lastName;
        this.confirmSocialModel.phoneNumber = '';
        this.confirmSocialModel.email = user.email;
        console.log('confirmSocialModel');
        console.log(this.confirmSocialModel);
      });
    }
  }

  private setComponentData(): void {
    if (this.data.fromGoogleAuth) {
      this.authType = 'Google';
    }
    this.vesselId = this.data.vesselId;
    if (this.data && this.data.userIdAfterAuth) {
      this.setAfterOauthState(this.data.userIdAfterAuth);
    }
  }

  private initForm(): void {
    this.model = {
      email: '',
      firstName: '',
      lastName: '',
      password: '',
      confirmPassword: '',
      phoneNumber: '',
      dobDay: null,
      dobMonth: '',
      dobYear: null,
    };
    this.signUpForm = new FormGroup({
      firstName: new FormControl('', [Validators.required]),
      lastName: new FormControl('', [Validators.required]),
      email: new FormControl('', [Validators.required, Validators.email]),
      password: new FormControl('', [Validators.required]),
      confirmPassword: new FormControl('', [Validators.required]),
      phoneNumber: new FormControl('', [Validators.required]),
      dob: new FormControl('', [Validators.required]),
    });
    this.loginForm = new FormGroup({
      userName: new FormControl('', [Validators.required]),
      password: new FormControl('', [Validators.required]),
    });
  }

  private getGoogleAuthUrl(): void {
    let flow = '';
    if (!this.vesselId) {
      flow = 'login';
    }
    const googleAuthUrlSub = this.authService
      .getGoogleAuthUrl(
        {
          vesselId: this.vesselId,
        },
        flow,
      )
      .subscribe((result) => {
        console.log(result);
        this.googleUrl = result.authUrl;
      });
    this.subs.push(googleAuthUrlSub);
  }

  private setTitleText(): void {
    this.data.completingProfile
      ? (this.titleText = 'Complete your profile to continue')
      : (this.titleText = 'Sign Up');
  }
}

export interface PhoneVerifyModel {
  code: string;
}

export interface ChangeNumberRequest {
  phoneNumber: string;
}

interface ISignUpFormModel {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  phoneNumber: string;
  dobMonth: string;
  dobDay: number;
  dobYear: number;
}

interface IConfirmSocialInfoModel {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  dobMonth: string;
  dobDay: number;
  dobYear: number;
}

export interface IFacebookAuthResponse {
  accessToken: string;
  data_access_expiration_time: number;
  expiresIn: number;
  graphDomain: string;
  signedRequest: string;
  userID: string;
}

export enum CustomerAccountDialogView {
  LOGIN = 'login',
  SIGNUP = 'signup',
  CONFIRM_SOCIAL_INFO = 'confirmSocialInfo',
  CONFIRM_PHONE_NUMBER = 'confirmPhoneNumber',
}

export const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function getFullMonthName(bdayString: string): string {
  if (!bdayString) {
    return '';
  }
  return months[parseInt(moment(bdayString).format('M')) - 1];
}

const validateEmail = (email) => {
  return String(email)
    .toLowerCase()
    .match(
      // tslint:disable-next-line:max-line-length
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );
};
